import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { externalLinks } from 'src/externalLinks';

interface GroupNavigationItem {
  icon: any;
  label: string;
  value: string;
  to?: string;
  component?: React.ReactNode;
}

interface GroupPageConfig {
  title: string;
  actions?: Array<{
    label: string;
    href?: string;
    target?: string;
    icon?: any;
  }>;
}

export const useGroupNavigation = (
  groupId?: string,
  groupData?: { cachedSlug?: string; name?: string; calendar?: boolean }
) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'group.editPage',
  });

  const getNavigationItems = (
    currentPage?: string,
    customComponents?: Record<string, React.ReactNode>
  ): GroupNavigationItem[] => {
    const baseItems = [
      {
        icon: icon({ name: 'user', style: 'regular' }),
        label: t('Details'),
        value: 'details',
        to: `/groups/${groupId}`,
        component: customComponents?.details,
      },
      {
        icon: icon({ name: 'cog', style: 'regular' }),
        label: t('Settings'),
        value: 'settings',
        to: `/groups/${groupId}`,
        component: customComponents?.settings,
      },
      {
        icon: icon({ name: 'credit-card', style: 'regular' }),
        label: t('Payment'),
        value: 'payment',
        to: `/groups/${groupId}`,
        component: customComponents?.payment,
      },
      {
        icon: icon({ name: 'youtube', style: 'brands' }),
        label: 'Videos',
        value: 'videos',
        to: `/groups/${groupId}/videos`,
      },
      {
        icon: icon({ name: 'file-text', style: 'regular' }),
        label: 'Resources',
        value: 'resources',
        to: `/groups/${groupId}/resources`,
      },
      {
        icon: icon({ name: 'pencil-square', style: 'regular' }),
        label: 'Assessments',
        value: 'assessments',
        to: `/groups/${groupId}/assessments`,
      },
    ];

    // Only include videos/resources/assessments for existing groups (not 'new')
    const isExistingGroup = groupId && groupId !== 'new';
    return isExistingGroup ? baseItems : baseItems.slice(0, 3);
  };

  const getPageConfig = (isNew?: boolean): GroupPageConfig => {
    const config: GroupPageConfig = {
      title: isNew ? t('title') : t('editTitle'),
    };

    // Add actions for existing groups only
    const isExistingGroup = groupId && groupId !== 'new';
    if (isExistingGroup && !isNew) {
      config.actions = [];

      // Add "View Group" action
      config.actions.push({
        label: t('View Group'),
        href: externalLinks.viewGroup(groupData?.cachedSlug, groupId),
        target: '_blank',
        icon: icon({ name: 'external-link', style: 'regular' }),
      });

      // Add "View Calendar" action if group has calendar
      if (groupData?.calendar) {
        config.actions.push({
          label: 'View Calendar',
          href: externalLinks.viewGroupCalendar(groupId),
          target: '_blank',
          icon: icon({ name: 'calendar', style: 'regular' }),
        });
      }
    }

    return config;
  };

  return {
    getNavigationItems,
    getPageConfig,
  };
};
